/* ==========================================================================
   AutocompleteRxjs > Client > Prototype
   ========================================================================== */

/* Import modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');

const { fromEvent } = require('rxjs');
const { debounceTime, map, tap, distinctUntilChanged, filter, switchMap } = require('rxjs/operators');

const { actions, types } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { startSuggestions, destroySuggestions } = require('./suggestions');
const { startInput } = require('./input');
const { initLoader, destroyLoader } = require('./loader');

const Services = require('./services');

const { NO_OPEN_SUGGESTIONS_KEYS } = require('../../constants');

/* Prototype */
const prototype = {
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            autocomplete: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            clear: () => this.store.dispatch(actions.clear()),
            destroy: () => this.store.dispatch(actions.destroy()),
            disable: () => this.store.dispatch(actions.disable()),
            display: () => this.store.dispatch(actions.display()),
            setUiState: data => this.store.dispatch(actions.setUiState(data)),
            enable: () => this.store.dispatch(actions.enable()),
            hide: () => this.store.dispatch(actions.hide()),
            setQuery: query => this.store.dispatch(actions.setQuery(query)),
            setValue: data => this.store.dispatch(actions.setValue(data))
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Destroy components */
                destroySuggestions(this);
                destroyLoader(this);

                /* Unsubscribe */
                this.unsubscribe();
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());

                /* Clean DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean reference */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),

            setValue: this.store.subscribe((state, action) => {
                if (action.type !== types.SET_VALUE) return;

                /* Data */
                const { text } = action.payload;
                const { active } = this.store.select('autocomplete');

                /* Update input */
                this.hooks.input.value = text;

                if (active) return;

                /* Toggle active */
                this.actions.toggleActive();
            }),
            disable: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE) return;

                const { css } = this.config;
                this.element.classList.add(css.disabled);
                this.hooks.input.setAttribute('disabled', '');
            }),
            enable: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE) return;

                const { css } = this.config;
                this.element.classList.remove(css.disabled);
                this.hooks.input.removeAttribute('disabled');
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                const { css } = this.config;
                this.element.classList.add(css.hidden);
            }),
            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                const { css } = this.config;
                this.element.classList.remove(css.hidden);
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        /* Build service */
        const { getSuggestions } = Services(this.config);

        this.subscriptions = {
            keyups: fromEvent(this.hooks.input, 'keyup')
                .pipe(
                    tap(event => event.stopPropagation()),
                    filter(event => !NO_OPEN_SUGGESTIONS_KEYS.some(key => key === event.keyCode)),
                    map(event => event.target.value),
                    distinctUntilChanged(),
                    debounceTime(200),
                    tap(value => {
                        /* Close suggestions */
                        const { openSuggestions } = this.store.select('autocomplete');
                        if (value && value.length < this.config.threshold && openSuggestions)
                            this.actions.closeSuggestions();
                    }),
                    filter(value => !!value && value.length >= this.config.threshold),
                    tap(value => this.actions.setQuery(value)),
                    switchMap(getSuggestions)
                )
                .subscribe(data => {
                    /* Data */
                    const { limit, hasAllAirpots } = this.config;
                    const { d = [], error: queryError } = data;
                    const { error } = this.store.select('autocomplete');

                    /* Set error */
                    if (queryError) {
                        if (!error) this.actions.setError();
                        this.actions.closeSuggestions();

                        return;
                    }

                    /* Remove error */
                    if (error) this.actions.resetStatus();

                    const list = !hasAllAirpots ? d.filter(item => item.id.split('-')[0] !== 'AEC') : d;

                    /* Dispatch action to set suggestions */
                    this.actions.setSuggestions(list.slice(0, limit));
                }),
            document: fromEvent(window.document, 'click')
                .pipe(
                    filter(event => !event.target.closest(`${this.selector}`)),
                    filter(() => this.store.select('autocomplete').openSuggestions),
                    map(() => {
                        const { suggestions, openSuggestions } = this.store.select('autocomplete');

                        return { suggestions, openSuggestions };
                    })
                )
                .subscribe(() => {
                    /* Close suggestions and sync input value on click outside */
                    this.actions.closeSuggestions();
                    this.actions.syncValue();
                })
        };

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        /* Start logic */
        const { value, hidden, active, disabled, isLoading } = this.config;

        /* Sync store */
        this.actions.setUiState({ hidden, active, disabled, isLoading });

        if (disabled) this.actions.disable();

        if (!value) return this;

        /* Dispatch action */
        this.actions.setValue(value);

        return this;
    },
    /* Init */
    init() {
        if (!this.config.init) return this;

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Start */
        startInput(this);
        startSuggestions(this);
        initLoader(this);

        this.start();

        return this;
    }
};

/* Exports */
module.exports = prototype;
